import { useForm } from 'antd/es/form/Form';
import { useCallback, useContext, useState } from 'react';

import {
  useLazyGetPnLReportQuery,
  type ModelFinancialPnlResult,
} from '@app/api/Analytics';
import { FINANCIAL_RESULTS_REPORT } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { DEFAULT_FILTERS_DATA } from '@app/pages/Analytics/data';
import type { FiltersInterface } from '@app/pages/Analytics/FinancialResultsReport/types';
import { useShownReportPeriod } from '@app/pages/Analytics/hooks';
import type { HandleSearchDataProps } from '@app/pages/Analytics/types';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

export const useFinancialResultsReport = () => {
  const [tableData, setTableData] = useState<ModelFinancialPnlResult>();

  const [form] = useForm<FiltersInterface>();

  const { setShownPeriod } = useShownReportPeriod(FINANCIAL_RESULTS_REPORT);
  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [getPnLReport, { isLoading }] = useLazyGetPnLReportQuery();

  const handleSearchData = useCallback(
    async ({ filterMonth, filterYear }: HandleSearchDataProps) => {
      try {
        const result = await getPnLReport({
          ActiveBranchId,
          filterMonth,
          filterYear,
        }).unwrap();

        setTableData(result);
        setShownPeriod({ filterMonth, filterYear });
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, getPnLReport, setShownPeriod, showNotification]
  );

  const handleReset = useCallback(() => {
    form.resetFields();

    handleSearchData(DEFAULT_FILTERS_DATA);
  }, [form, handleSearchData]);

  const handleSubmitForm = useCallback(
    (values: FiltersInterface) => {
      const { filterMonth, filterYear } = values;
      handleSearchData({
        filterMonth,
        filterYear,
      });
    },
    [handleSearchData]
  );

  return {
    handleReset,
    form,
    handleSubmitForm,
    tableData,
    handleSearchData,
    isLoading,
  };
};
